import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, CheckCircle, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import WaveformVisualizer from './WaveformVisualizer';
import { apiService } from '../../services/apiService';
import { deviceManager } from '../../services/deviceManager'; // Import DeviceManager

const VerificationContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const TestCard = styled.div`
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const TestPhrase = styled.div`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 2px dashed ${({ theme }) => theme.colors.primary[500]}40;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const PhraseText = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary[400]};
  margin: 0;
`;

const HintText = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const RecordingArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const VisualizerBox = styled(motion.div)`
  width: 100%;
  height: 150px;
  background: ${({ theme }) => theme.colors.dark.bg};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const RecordButton = styled(motion.button)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: none;
  background: ${({ isRecording, theme }) => 
    isRecording 
      ? `linear-gradient(135deg, ${theme.colors.accent.red}, ${theme.colors.accent.orange})`
      : `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent.purple})`
  };
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  box-shadow: 0 4px 20px ${({ theme }) => theme.colors.primary[500]}40;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 6px 30px ${({ theme }) => theme.colors.primary[500]}60;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const ResultCard = styled(motion.div)`
  background: ${({ success, theme }) => 
    success 
      ? `${theme.colors.status.online}10`
      : `${theme.colors.accent.red}10`
  };
  border: 1px solid ${({ success, theme }) => 
    success 
      ? theme.colors.status.online
      : theme.colors.accent.red
  };
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
`;

const ResultIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ success, theme }) => 
    success 
      ? theme.colors.status.online
      : theme.colors.accent.red
  };
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  
  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const AccuracyScore = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ success, theme }) => 
    success 
      ? theme.colors.status.online
      : theme.colors.accent.red
  };
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ResultText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const RetryButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  margin: ${({ theme }) => theme.spacing.xl} auto 0;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.accent.red};
    color: ${({ theme }) => theme.colors.accent.red};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.accent.red}20;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const RedirectingText = styled(motion.p)`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.md};
  font-style: italic;
`;

const VoiceVerification = ({ enrollmentData, onComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    initializeMicrophone();
    
    // Listen for device changes
    const handleDeviceChange = () => {
      console.log('🔄 [VoiceVerification] Device change detected');
      // Reinitialize audio stream if needed
      initializeMicrophone();
    };
    
    deviceManager.on('deviceChange', handleDeviceChange);
    
    return () => {
      deviceManager.removeListener('deviceChange', handleDeviceChange);
      cleanupMicrophone();
    };
  }, []);

  // Auto-redirect on successful verification
  useEffect(() => {
    if (verificationResult?.success) {
      const timer = setTimeout(() => {
        onComplete(true);
      }, 3000); // Redirect after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [verificationResult, onComplete]);

  const initializeMicrophone = async () => {
    try {
      // Use DeviceManager to get audio stream
      const stream = deviceManager.getAudioStream();
      if (stream) {
        setAudioStream(stream);
      } else {
        // If no stream available, request access through DeviceManager
        await deviceManager.initialize();
        const newStream = deviceManager.getAudioStream();
        setAudioStream(newStream);
      }
    } catch (error) {
      console.error('Microphone initialization error:', error);
    }
  };

  const cleanupMicrophone = () => {
    if (audioStream) {
      // Don't stop the stream as DeviceManager manages it
      setAudioStream(null);
    }
  };

  const requestMicrophoneAccess = async () => {
    try {
      // Use DeviceManager to ensure microphone access
      await deviceManager.initialize();
      const stream = deviceManager.getAudioStream();
      setAudioStream(stream);
    } catch (error) {
      console.error('Microphone access error:', error);
    }
  };

  const startVerification = async () => {
    if (!audioStream) {
      await requestMicrophoneAccess();
      return;
    }

    setIsRecording(true);
    setVerificationResult(null);
    audioChunksRef.current = [];

    const mediaRecorder = new MediaRecorder(audioStream, {
      mimeType: 'audio/webm;codecs=opus'
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      processVerification(audioBlob);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();

    // Auto-stop after 2 seconds
    setTimeout(() => {
      if (mediaRecorderRef.current?.state === 'recording') {
        stopVerification();
      }
    }, 2000);
  };

  const stopVerification = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processVerification = async (audioBlob) => {
    setIsProcessing(true);

    try {
      const token = localStorage.getItem('sarvik_token');
      const result = await apiService.verifyVoice(audioBlob, token);
      
      console.log('Verification result:', result); // Debug log
      
      setIsProcessing(false);
      
      // Check if result has the expected structure
      if (result && typeof result === 'object') {
        setVerificationResult({
          success: result.success || false,
          accuracy: result.confidence || result.similarity * 100 || 0,
          message: result.success 
            ? "Voice verification successful! Your profile is ready."
            : result.reason || `Voice match confidence: ${result.confidence || 0}%`
        });
        
        // Store voice profile locally if successful
        if (result.success) {
          const profile = await apiService.getVoiceProfile(token);
          if (profile) {
            const user = JSON.parse(localStorage.getItem('sarvik_user') || '{}');
            localStorage.setItem(`voice_profile_${user.id}`, JSON.stringify(profile));
          }
        }
      } else {
        // Fallback for unexpected response
        setVerificationResult({
          success: false,
          accuracy: 0,
          message: "Verification failed. Please try again."
        });
      }
    } catch (error) {
      console.error('Verification failed:', error);
      setIsProcessing(false);
      setVerificationResult({
        success: false,
        accuracy: 0,
        message: "Verification failed. Please try again."
      });
    }
  };

  const handleRetry = () => {
    setVerificationResult(null);
    setIsProcessing(false);
  };

  return (
    <VerificationContainer>
      <Title>Voice Verification</Title>
      <Description>
        Final step: Let's verify your voice profile is working correctly.
      </Description>

      <TestCard>
        {!verificationResult && (
          <TestPhrase>
            <PhraseText>Hey SARVIK</PhraseText>
            <HintText>Say this phrase one  time to complete setup</HintText>
          </TestPhrase>
        )}

        <RecordingArea>
          <AnimatePresence mode="wait">
            {/* Show visualizer only when recording or processing */}
            {(isRecording || isProcessing) && (
              <VisualizerBox
                key="visualizer"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 150, opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <WaveformVisualizer 
                  audioStream={audioStream} 
                  isProcessing={isProcessing}
                  processingText={isProcessing ? "Analyzing voice..." : null}
                />
              </VisualizerBox>
            )}

            {/* Show mic button when ready to record */}
            {!isRecording && !isProcessing && !verificationResult && (
              <motion.div
                key="button"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <RecordButton
                  isRecording={false}
                  onClick={startVerification}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={!audioStream}
                >
                  <Mic />
                </RecordButton>
              </motion.div>
            )}

            {/* Show result card */}
            {verificationResult && (
              <ResultCard
                key="result"
                success={verificationResult.success}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ResultIcon success={verificationResult.success}>
                  {verificationResult.success ? <CheckCircle /> : <XCircle />}
                </ResultIcon>
                <AccuracyScore success={verificationResult.success}>
                  {Math.round(verificationResult.accuracy)}% Match
                </AccuracyScore>
                <ResultText>{verificationResult.message}</ResultText>
                
                {verificationResult.success && (
                  <RedirectingText
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    Redirecting to dashboard...
                  </RedirectingText>
                )}
              </ResultCard>
            )}
          </AnimatePresence>
        </RecordingArea>

        {/* Show retry button only on failure */}
        {verificationResult && !verificationResult.success && (
          <RetryButton 
            onClick={handleRetry}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw />
            Try Again
          </RetryButton>
        )}
      </TestCard>
    </VerificationContainer>
  );
};

export default VoiceVerification;
